import { useCallback, useEffect, useMemo, useState } from 'react'

/*
 * Two small things the Command Center needs everywhere: a section that folds
 * away and remembers, and a table that does not render four hundred rows.
 *
 * The page grew to 410 rows across its tabs. Nothing there is unwanted — it is
 * the whole model — but a screen you scroll past is a screen you stop reading,
 * and the blocker at the top is the part that must never be scrolled past.
 */

/** Remembered per key, per browser. Never throws: private windows deny access. */
function useRemembered(key: string, fallback: boolean): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const held = localStorage.getItem(key)
      return held === null ? fallback : held === '1'
    } catch {
      return fallback
    }
  })
  const set = useCallback((v: boolean) => {
    setValue(v)
    try {
      localStorage.setItem(key, v ? '1' : '0')
    } catch {
      /* the preference simply will not persist */
    }
  }, [key])
  return [value, set]
}

export function Collapsible({
  id, title, count, defaultOpen = false, children,
}: {
  id: string
  title: string
  /** Shown beside the title so a folded section still says how much is in it. */
  count?: number | string | null
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useRemembered(`mc.open.${id}`, defaultOpen)
  return (
    <section className={`fold ${open ? 'is-open' : ''}`}>
      <button type="button" className="fold-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="fold-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="fold-title">{title}</span>
        {count !== undefined && count !== null && <span className="fold-count">{count}</span>}
      </button>
      {/* Unmounted when closed, not merely hidden: a folded section should cost
          nothing to have on the page. */}
      {open && <div className="fold-body">{children}</div>}
    </section>
  )
}

const PAGE = 12

export function usePaged<T>(rows: T[], size = PAGE) {
  const [page, setPage] = useState(0)
  const [all, setAll] = useState(false)
  // A filter that shortens the list must not leave you stranded on page 4.
  useEffect(() => { setPage(0) }, [rows.length])
  const pages = Math.max(1, Math.ceil(rows.length / size))
  const shown = useMemo(() => (all ? rows : rows.slice(page * size, page * size + size)), [rows, page, all, size])
  return { shown, page, pages, setPage, all, setAll, total: rows.length, size }
}

export function Pager({ p }: { p: ReturnType<typeof usePaged<unknown>> }) {
  if (p.total <= p.size) return null
  return (
    <div className="pager">
      <button type="button" onClick={() => p.setPage(Math.max(0, p.page - 1))} disabled={p.all || p.page === 0}>‹ prev</button>
      <span className="pager-at">
        {p.all ? `all ${p.total}` : `${p.page * p.size + 1}–${Math.min(p.total, (p.page + 1) * p.size)} of ${p.total}`}
      </span>
      <button type="button" onClick={() => p.setPage(Math.min(p.pages - 1, p.page + 1))} disabled={p.all || p.page >= p.pages - 1}>next ›</button>
      <button type="button" className="link" onClick={() => p.setAll(!p.all)}>{p.all ? 'paginate' : 'show all'}</button>
    </div>
  )
}

/** A checkbox that hides finished rows, and says how many it hid. */
export function HideDone({
  id, label, hidden, on, set,
}: { id: string; label: string; hidden: number; on: boolean; set: (v: boolean) => void }) {
  return (
    <label className="hide-done">
      <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} id={id} />
      {label}
      {on && hidden > 0 && <span className="cc-sub">{hidden} hidden</span>}
    </label>
  )
}

export function useHideDone(id: string, fallback = true) {
  return useRemembered(`mc.hide.${id}`, fallback)
}
